// Fallback atomic composition for Atomic Mode.
//
// ~40% of the material database has no explicit `composition:` (wood, concrete,
// flesh, plastics, fire, logic gates, …). For Atomic Mode we still want *every*
// material to deconstruct into atoms, so this module assigns a chemically
// sensible representative composition when the YAML doesn't provide one.
//
// The resolution order is: explicit composition (handled by the caller) →
// name match → tag match → category default → phase default → carbon. Each
// fallback is a real, recognisable formula (cellulose for wood, SiO2 for
// glass/sand, CaCO3 for stone/shell, a generic hydrocarbon for fuels, a
// water-rich mix for living tissue), so the resulting LAMMPS system reflects
// what the material actually is rather than a single filler element.
//
// These are deliberately *representative*, not exact — a sandbox "concrete"
// cell isn't a specific mix design. The goal is plausible atoms for every
// material, which is what Atomic Mode needs.

export interface SpeciesRef {
  id: string;
  n: number;
}

// Common building-block formulas reused across several materials.
const CELLULOSE: SpeciesRef[] = [
  { id: "C", n: 6 },
  { id: "H", n: 10 },
  { id: "O", n: 5 },
]; // (C6H10O5)n — wood, paper, cotton, plant matter
const HYDROCARBON: SpeciesRef[] = [
  { id: "C", n: 1 },
  { id: "H", n: 2 },
]; // (CH2)n — fuels, oils, waxes, plastics, rubber
const WATER: SpeciesRef[] = [
  { id: "H", n: 2 },
  { id: "O", n: 1 },
];
const SILICA: SpeciesRef[] = [
  { id: "Si", n: 1 },
  { id: "O", n: 2 },
]; // SiO2 — sand, glass, quartz, granite-ish, ceramic
const CALCIUM_CARBONATE: SpeciesRef[] = [
  { id: "Ca", n: 1 },
  { id: "C", n: 1 },
  { id: "O", n: 3 },
]; // CaCO3 — stone, shell, bone mineral, concrete-ish
const PROTEIN: SpeciesRef[] = [
  { id: "C", n: 4 },
  { id: "H", n: 6 },
  { id: "N", n: 1 },
  { id: "O", n: 2 },
]; // rough average amino-acid residue — flesh, hair, leather, skin
const TISSUE: SpeciesRef[] = [
  { id: "O", n: 16 },
  { id: "C", n: 10 },
  { id: "H", n: 22 },
  { id: "N", n: 1 },
]; // water-dominated living tissue (≈65% O by mass)
const AIR: SpeciesRef[] = [
  { id: "N", n: 4 },
  { id: "O", n: 1 },
]; // smoke/steam/gas filler ≈ air
const PLASMA: SpeciesRef[] = [{ id: "H", n: 1 }]; // ionised gas / fire / spark — treat as light ionised H

// Exact name → composition overrides (checked first). Keys match material ids.
const BY_ID: Record<string, SpeciesRef[]> = {
  // fuels / hydrocarbons
  oil: HYDROCARBON,
  gasoline: HYDROCARBON,
  diesel: HYDROCARBON,
  kerosene: HYDROCARBON,
  vegetable_oil: HYDROCARBON,
  wax: HYDROCARBON,
  asphalt: HYDROCARBON,
  napalm: HYDROCARBON,
  rocket_fuel: HYDROCARBON,
  rubber: HYDROCARBON,
  silicone: [
    { id: "Si", n: 1 },
    { id: "O", n: 1 },
    { id: "C", n: 2 },
    { id: "H", n: 6 },
  ],
  // plastics ≈ (CH2)n with heteroatoms
  plastic_pet: [
    { id: "C", n: 10 },
    { id: "H", n: 8 },
    { id: "O", n: 4 },
  ],
  plastic_pvc: [
    { id: "C", n: 2 },
    { id: "H", n: 3 },
    { id: "Cl", n: 1 },
  ],
  plastic_hdpe: HYDROCARBON,
  foam: HYDROCARBON,
  kevlar: [
    { id: "C", n: 14 },
    { id: "H", n: 10 },
    { id: "N", n: 2 },
    { id: "O", n: 2 },
  ],
  epoxy: [
    { id: "C", n: 21 },
    { id: "H", n: 25 },
    { id: "O", n: 5 },
  ],
  // cellulosic / plant
  wood: CELLULOSE,
  soaked_wood: CELLULOSE,
  oil_wood: CELLULOSE,
  kerosene_wood: CELLULOSE,
  gasoline_wood: CELLULOSE,
  paper: CELLULOSE,
  fabric: CELLULOSE,
  rope: CELLULOSE,
  waxed_rope: CELLULOSE,
  plant: CELLULOSE,
  seed: CELLULOSE,
  leaf: CELLULOSE,
  flour: CELLULOSE,
  peat: CELLULOSE,
  torch: CELLULOSE,
  candle: HYDROCARBON,
  bonfire: CELLULOSE,
  // minerals / stone / ceramics
  granite: SILICA,
  basalt: SILICA,
  hot_basalt: SILICA,
  pumice: SILICA,
  mica: SILICA,
  clay: [
    { id: "Al", n: 2 },
    { id: "Si", n: 2 },
    { id: "O", n: 9 },
    { id: "H", n: 4 },
  ],
  talc: [
    { id: "Mg", n: 3 },
    { id: "Si", n: 4 },
    { id: "O", n: 12 },
    { id: "H", n: 2 },
  ],
  emerald: [
    { id: "Be", n: 3 },
    { id: "Al", n: 2 },
    { id: "Si", n: 6 },
    { id: "O", n: 18 },
  ],
  brick: SILICA,
  ceramic: SILICA,
  concrete: CALCIUM_CARBONATE,
  cement: CALCIUM_CARBONATE,
  stone: SILICA,
  gravel: SILICA,
  soil: SILICA,
  magma: SILICA,
  lava: SILICA,
  ash: [
    { id: "K", n: 2 },
    { id: "Ca", n: 1 },
    { id: "C", n: 1 },
    { id: "O", n: 3 },
  ],
  dust: SILICA,
  mud: [
    { id: "Si", n: 1 },
    { id: "O", n: 2 },
    { id: "H", n: 2 },
  ],
  // biological tissue
  blood: TISSUE,
  flesh: PROTEIN,
  fat: HYDROCARBON,
  hair: PROTEIN,
  bone: CALCIUM_CARBONATE,
  leather: PROTEIN,
  bacteria: TISSUE,
  virus: PROTEIN,
  mold: TISSUE,
  fungus: TISSUE,
  slime: TISSUE,
  soap: [
    { id: "C", n: 18 },
    { id: "H", n: 35 },
    { id: "Na", n: 1 },
    { id: "O", n: 2 },
  ],
  // explosives / energetics
  black_powder: [
    { id: "K", n: 1 },
    { id: "N", n: 1 },
    { id: "O", n: 3 },
    { id: "S", n: 1 },
    { id: "C", n: 1 },
  ],
  glow_powder: [{ id: "P", n: 1 }],
  // gases / vapours
  smoke: AIR,
  // electronics — silicon-based
  semiconductor_n: [{ id: "Si", n: 1 }],
  semiconductor_p: [{ id: "Si", n: 1 }],
};

// Tag → composition (checked after id). First matching tag wins.
const BY_TAG: Array<[string, SpeciesRef[]]> = [
  ["plant_food", CELLULOSE],
  ["accelerant", HYDROCARBON],
  ["semiconductor", [{ id: "Si", n: 1 }]],
];

// Category → composition (checked after tags).
const BY_CATEGORY: Record<string, SpeciesRef[]> = {
  biological: TISSUE,
  mineral: SILICA,
  electronic: [{ id: "Si", n: 1 }],
  powder: SILICA,
  structure: CELLULOSE,
  plasma: PLASMA,
};

// Phase → last-resort composition (checked after category).
const BY_PHASE: Record<string, SpeciesRef[]> = {
  gas: AIR,
  plasma: PLASMA,
  liquid: WATER,
  solid: [{ id: "C", n: 1 }],
};

export interface MaterialLike {
  id: string;
  name?: string;
  phase?: string;
  category?: string;
  tags?: string[];
}

/**
 * Resolve a representative atomic composition for a material that has no
 * explicit one. Always returns at least one species, so every material can be
 * turned into atoms. `null` is never returned — the final fallback is carbon.
 */
export function fallbackComposition(material: MaterialLike): SpeciesRef[] {
  const byId = BY_ID[material.id];
  if (byId) return byId;

  const tags = material.tags ?? [];
  for (const [tag, comp] of BY_TAG) {
    if (tags.includes(tag)) return comp;
  }

  const cat = material.category;
  if (cat && BY_CATEGORY[cat]) return BY_CATEGORY[cat];

  const phase = material.phase;
  if (phase && BY_PHASE[phase]) return BY_PHASE[phase];

  return [{ id: "C", n: 1 }];
}
